/**
 * The platforms in the share box, and what tapping one can honestly do.
 *
 * No web page can push a video FILE into another site's composer — that is a browser rule, not
 * a CherryClipz limitation. So a platform button does the most it can, which differs by device:
 *
 *   phone   → trim, then open the system share sheet. The user taps the app there and the video
 *             really is attached. One extra tap, full result.
 *   desktop → trim, save the file, and open that platform's composer in a new tab with the
 *             caption filled in. The clip is in Downloads, ready to drag into the post.
 *
 * `web` is the composer URL builder, or null where the platform has no web composer at all
 * (Instagram and TikTok are upload-from-the-app only). Those two are phone-only and say so
 * rather than appearing and failing.
 */

const enc = encodeURIComponent;

/** Brand marks as path data, so they render crisply at any size instead of as emoji. */
export const MARKS = {
  whatsapp: "M17.5 14.4c-.3-.2-1.7-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.7 1-.9 1.2-.2.2-.3.2-.6.1-1.6-.8-2.7-1.5-3.8-3.4-.3-.5.3-.5.8-1.5.1-.2 0-.4 0-.5 0-.2-.7-1.6-.9-2.2-.2-.5-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.2.2 2.1 3.2 5.1 4.4 1.9.8 2.6.9 3.6.7.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.6-.3z M12 2a10 10 0 0 0-8.6 15l-1.3 4.8 5-1.3A10 10 0 1 0 12 2zm0 18.3a8.3 8.3 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.3 8.3 0 1 1 12 20.3z",
  instagram: "M12 2.2c3.2 0 3.6 0 4.9.1 1.2.1 1.8.2 2.2.4.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.2.4.4 1 .4 2.2.1 1.3.1 1.7.1 4.9s0 3.6-.1 4.9c-.1 1.2-.2 1.8-.4 2.2-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.2-1 .4-2.2.4-1.3.1-1.7.1-4.9.1s-3.6 0-4.9-.1c-1.2-.1-1.8-.2-2.2-.4a3.9 3.9 0 0 1-1.4-.9 3.9 3.9 0 0 1-.9-1.4c-.2-.4-.4-1-.4-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.9c.1-1.2.2-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1-.4 2.2-.4C8.4 2.2 8.8 2.2 12 2.2zm0 3.2A6.6 6.6 0 1 0 18.6 12 6.6 6.6 0 0 0 12 5.4zm0 10.9A4.3 4.3 0 1 1 16.3 12 4.3 4.3 0 0 1 12 16.3zm6.9-11.1a1.5 1.5 0 1 1-1.5-1.6 1.6 1.6 0 0 1 1.5 1.6z",
  facebook: "M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12z",
  x: "M18.2 2.2h3.3l-7.2 8.3L22.8 22h-6.6l-5.2-6.8L5 22H1.7l7.7-8.8L1.2 2.2H8l4.7 6.2zm-1.2 17.8h1.8L7.1 4.1H5.1z",
  linkedin: "M20.4 2H3.6A1.6 1.6 0 0 0 2 3.6v16.8A1.6 1.6 0 0 0 3.6 22h16.8a1.6 1.6 0 0 0 1.6-1.6V3.6A1.6 1.6 0 0 0 20.4 2zM8.1 18.7H5.2V9.4h2.9zM6.7 8.1a1.7 1.7 0 1 1 1.7-1.7 1.7 1.7 0 0 1-1.7 1.7zm12 10.6h-2.9v-4.5c0-1.1 0-2.5-1.5-2.5s-1.8 1.2-1.8 2.4v4.6H9.6V9.4h2.8v1.3a3.1 3.1 0 0 1 2.8-1.5c3 0 3.5 2 3.5 4.5z",
  tiktok: "M16.6 5.8a4.8 4.8 0 0 1-1-2.8h-3.2v12.9a2.6 2.6 0 1 1-2.6-2.6c.3 0 .5 0 .8.1v-3.3a6 6 0 0 0-.8-.1 5.9 5.9 0 1 0 5.9 5.9V9.4a7.9 7.9 0 0 0 4.6 1.5V7.7a4.6 4.6 0 0 1-3.7-1.9z",
};

export const TARGETS = [
  {
    key: "whatsapp",
    name: "WhatsApp",
    color: "#1fa855",
    glyph: "💬",
    web: ({ text, url }) => `https://wa.me/?text=${enc(`${text}\n${url}`)}`,
  },
  {
    key: "facebook",
    name: "Facebook",
    color: "#1877f2",
    glyph: "f",
    web: ({ url }) => `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
  },
  {
    key: "instagram",
    name: "Instagram",
    color: "linear-gradient(45deg,#f9a03f,#d6249f 55%,#8a3ab9)",
    glyph: "📷",
    web: null, // no web composer — app upload only
  },
  {
    key: "x",
    name: "X",
    color: "#000000",
    glyph: "𝕏",
    web: ({ text, url }) => `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`,
  },
  {
    key: "linkedin",
    name: "LinkedIn",
    color: "#0a66c2",
    glyph: "in",
    web: ({ url }) => `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
  },
  {
    key: "tiktok",
    name: "TikTok",
    color: "#111111",
    glyph: "♪",
    web: null, // no web composer — app upload only
  },
];

export const targetByKey = (key) => TARGETS.find((t) => t.key === key) || null;

/**
 * Is this a phone or tablet? A coarse pointer means the share sheet is the REAL one, with
 * WhatsApp and Instagram in it.
 *
 * Deliberately not `canShareFiles()`: a Mac answers yes to that and then opens a sheet
 * containing AirDrop, Mail and Notes — no social apps at all. Routing a WhatsApp button into
 * that sheet is why tapping WhatsApp appeared to do nothing (Isaac, 2026-09-24).
 */
export function isPhoneLike() {
  try {
    return typeof window !== "undefined" && window.matchMedia
      && window.matchMedia("(pointer: coarse)").matches;
  } catch (e) {
    return false;
  }
}

/**
 * What a given platform button will actually do here.
 *
 * @param {object} target one of TARGETS
 * @param {boolean} canShareFile whether this browser can hand a file to another app
 * @returns {'sheet'|'composer'|'unavailable'}
 */
export function targetAction(target, canShareFile) {
  if (!target) return "unavailable";
  // Only a real phone's sheet contains the social apps.
  if (canShareFile && isPhoneLike()) return "sheet";
  if (target.web) return "composer";       // a laptop can at least open the composer
  return "unavailable";                    // app-only platform, on a laptop
}

/** One line telling the user what is about to happen, so the button never surprises them. */
export function targetPromise(target, canShareFile) {
  switch (targetAction(target, canShareFile)) {
    case "sheet":
      return `Cuts the clip, then opens your share sheet — pick ${target.name} there and the video comes with it.`;
    case "composer":
      return `Cuts the clip to your Downloads and opens ${target.name} in a new tab. It can take the words, not the file — you attach that yourself.`;
    default:
      return `${target.name} only takes uploads from inside its own app. Open this page on your phone to post there.`;
  }
}
